import React, { useState, useEffect, useCallback } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import ChatInterface from "./pages/ChatInterface";
import Login from "./pages/Login";
import LogoManagement from "./pages/LogoManagement";
import DocumentUpload from "./pages/DocumentUpload";
import KnowledgeGraph from "./pages/KnowledgeGraph";
import WidgetIntegration from "./pages/WidgetIntegration";
import DomainManagement from "./pages/DomainManagement";
import PromptVersioning from "./pages/PromptVersioning";
import ApiToolsManagement from "./pages/ApiToolsManagement";
import WebhookManagement from "./pages/WebhookManagement";
import Integrations from "./pages/Integrations";
import FreshchatConfig from "./pages/FreshchatConfig";
import TelegramConfig from "./pages/TelegramConfig";
import InstagramConfig from "./pages/InstagramConfig";
import InstagramOwnAppConfig from "./pages/InstagramOwnAppConfig";
import WhatsAppConfig from "./pages/WhatsAppConfig";
import WhatsAppTemplates from "./pages/WhatsAppTemplates";
import SlackConfig from "./pages/SlackConfig";
import EmailConfig from "./pages/EmailConfig";
import ZendeskConfig from "./pages/ZendeskConfig";
import FreshdeskConfig from "./pages/FreshdeskConfig";
import ShopifyConfig from "./pages/ShopifyConfig";
import KnowledgeBase from "./pages/KnowledgeBase";
import Documentation from "./pages/Documentation";
import WidgetPreview from "./pages/WidgetPreview";
import WidgetPreviewDisplay from "./pages/WidgetPreviewDisplay";
import AiAgents from "./pages/AiAgents";
import HumanAgents from "./pages/HumanAgents";
import HumanAgentGroups from "./pages/HumanAgentGroups";
import Analytics from "./pages/Analytics";
import CampaignManagement from "./pages/CampaignManagement";
import BusinessFlows from "./pages/BusinessFlows";
import UserManagement from "./pages/UserManagement";
import Contacts from "./pages/Contacts";
import ContactDetail from "./pages/ContactDetail";
import ManageFields from "./pages/ManageFields";
import Segments from "./pages/Segments";
import Reports from "./pages/Reports";
import HumanAgentChatInterface from "./pages/HumanAgentChatInterface";
import AdminDashboard from "./pages/AdminDashboard";
import Skills from "./pages/Skills";
import PaymentPage from "./components/payment/PaymentPage";
import api from "./utils/api";
import FlowBuilder from "./pages/FlowBuilder";
import Evaluations from "./pages/Evaluations";

// Global font styles
const globalStyles = `
  body {
    font-family: 'Proxima Nova', 'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  }
`;

if (typeof document !== "undefined") {
  const styleElement = document.createElement("style");
  styleElement.textContent = globalStyles;
  document.head.appendChild(styleElement);
}

/* ---------------------------------------------------------
   FIXED — Moved AdminPanelWrapper OUTSIDE AppContent
--------------------------------------------------------- */
function AdminPanelWrapper({ onLogout, user, activeAiAgentId, aiAgents }) {
  const navigate = useNavigate();

  const handleAdminPanel = () => {
    navigate("/admin");
  };

  return (
    <ChatInterface
      fullscreen={true}
      onLogout={onLogout}
      onAdminPanel={handleAdminPanel}
      user={user}
      activeAiAgentId={activeAiAgentId}
      aiAgents={aiAgents}
    />
  );
}

/* ---------------------------------------------------------
   MAIN APP CONTENT
--------------------------------------------------------- */

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("logo");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return typeof window !== "undefined" ? window.innerWidth < 768 : false;
  });
  const [activeAiAgentId, setActiveAiAgentId] = useState(() => {
    const saved = localStorage.getItem("activeAiAgentId");
    if (saved && saved !== "None" && saved !== "null" && saved !== "undefined") {
      return saved;
    }
    try {
      const savedUser = sessionStorage.getItem("user") || localStorage.getItem("user");
      if (savedUser) {
        const u = JSON.parse(savedUser);
        const assigned = u.assigned_ai_agents || [];
        if (assigned.length > 0) return String(assigned[0]);
        if (u.ai_agent_id) return String(u.ai_agent_id);
      }
    } catch (e) { }
    return "";
  });

  const [activeProduct, setActiveProductState] = useState(() => {
    return localStorage.getItem("activeProduct") || "agents";
  });

  const setActiveProduct = (p) => {
    setActiveProductState(p);
    localStorage.setItem("activeProduct", p);
  };

  const [aiAgents, setAiAgents] = useState([]);

  const [activeChatsCount, setActiveChatsCount] = useState(0);

  const [user, setUser] = useState(() => {
    const saved = sessionStorage.getItem("user") || localStorage.getItem("user");
    if (saved) {
      try {
        sessionStorage.setItem("user", saved);
        return JSON.parse(saved);
      } catch (e) { }
    }
    return null;
  });

  const isHumanAgent = user && user.role === 'human_agent';

  // Automatically sync updated role and user info from server on mount
  useEffect(() => {
    const syncUserProfile = async () => {
      const saved = sessionStorage.getItem("user") || localStorage.getItem("user");
      if (!saved) return;
      try {
        const me = await api.request("/auth/me");
        if (me && me.role) {
          const currentStored = JSON.parse(saved);
          if (currentStored.role !== me.role) {
            const updated = {
              ...currentStored,
              role: me.role,
              username: me.username,
              tenant_id: me.tenant_id,
              assigned_ai_agents: me.assigned_ai_agents || currentStored.assigned_ai_agents || []
            };
            sessionStorage.setItem("user", JSON.stringify(updated));
            localStorage.setItem("user", JSON.stringify(updated));
            setUser(updated);
          }
        }
      } catch (e) {
        // Silent catch for network or unauthenticated sessions
      }
    };
    syncUserProfile();
  }, []);

  const handleLogin = () => {
    const saved = sessionStorage.getItem("user") || localStorage.getItem("user");
    const userData = saved ? JSON.parse(saved) : { authenticated: true };
    setUser(userData);

    const assigned = userData.assigned_ai_agents || [];
    const isHumanAgentRole = userData.role === 'human_agent';
    const SYSTEM_DEFAULT_ID = "b8d8cf9e-3b2d-4f10-91a5-e3d81b2f7c03";

    if (isHumanAgentRole) {
      const firstAiAgent = assigned.length > 0 ? String(assigned[0]) : String(userData.ai_agent_id || SYSTEM_DEFAULT_ID);
      if (firstAiAgent) {
        setActiveAiAgentId(firstAiAgent);
        localStorage.setItem("activeAiAgentId", firstAiAgent);
      }
    } else if (assigned.includes(SYSTEM_DEFAULT_ID)) {
      setActiveAiAgentId(SYSTEM_DEFAULT_ID);
      localStorage.setItem("activeAiAgentId", SYSTEM_DEFAULT_ID);
    } else if (assigned.length > 0) {
      const firstAiAgent = String(assigned[0]);
      setActiveAiAgentId(firstAiAgent);
      localStorage.setItem("activeAiAgentId", firstAiAgent);
    } else if (userData.ai_agent_id) {
      const targetAiAgentId = String(userData.ai_agent_id);
      setActiveAiAgentId(targetAiAgentId);
      localStorage.setItem("activeAiAgentId", targetAiAgentId);
    } else {
      setActiveAiAgentId(SYSTEM_DEFAULT_ID);
      localStorage.setItem("activeAiAgentId", SYSTEM_DEFAULT_ID);
    }

    // Auto-redirect based on role: send users to copilot (/chat) instead of admin dashboard
    if (isHumanAgentRole) {
      navigate('/human-agent-chat');
    } else {
      navigate('/chat');
    }
  };

  // Enforce STRICT redirection: Agents/Users are ONLY allowed in their respective interfaces
  useEffect(() => {
    if (user && user.role !== 'admin' && user.role !== 'super_admin') {
      const isHumanAgentRole = user.role === 'human_agent';
      const isUser = user.role === 'user';

      // Whitelist of allowed paths
      const allowedPaths = isHumanAgentRole
        ? ['/human-agent-chat', '/login', '/docs', '/data-deletion', '/data-deletion-instructions']
        : isUser
          ? ['/chat', '/login', '/docs', '/data-deletion', '/data-deletion-instructions']
          : ['/login', '/docs', '/data-deletion', '/data-deletion-instructions'];

      const isAllowed = allowedPaths.some(path =>
        location.pathname === path || location.pathname.startsWith(path + '/')
      );

      if (!isAllowed) {
        console.warn(`🚫 RBAC: Redirecting ${user.role} from unauthorized path: ${location.pathname}`);
        navigate(isHumanAgentRole ? '/human-agent-chat' : isUser ? '/chat' : '/login');
      }
    } else if (user && user.role === 'admin') {
      // Scoped admin cannot access Super Admin-only routes
      const restrictedPaths = ['/billing', '/user-management'];
      const isRestricted = restrictedPaths.some(path =>
        location.pathname === path || location.pathname.startsWith(path + '/')
      );
      if (isRestricted) {
        console.warn(`🚫 RBAC: Redirecting scoped admin from super-admin only path: ${location.pathname}`);
        navigate('/chat');
      }
    }
  }, [user, navigate, location.pathname]);

  const handleLogout = async () => {
    try {
      // Tell the server to invalidate this token immediately (stamps last_logout_at)
      await api.request("/auth/logout", { method: "POST" });
    } catch (e) {
      // If the server call fails (network error, token already expired etc.),
      // we still proceed with the client-side cleanup — never block the user.
      console.warn("Server logout notification failed:", e);
    } finally {
      setUser(null);
      // Nuke ALL stored state so nothing leaks between accounts
      localStorage.clear();
      sessionStorage.clear();
      navigate('/login');
    }
  };

  const fetchAiAgents = useCallback(async () => {
    try {
      const data = await api.request("/ai-agents");
      if (data && data.agents) {
        setAiAgents(data.agents);
      }
    } catch (error) {
      console.error("Error fetching agents:", error);
    }
  }, []); // Decoupled from activeAiAgentId to prevent reload loops

  const fetchActiveChatsCount = useCallback(async () => {
    try {
      const data = await api.request("/analytics/active-chats");
      if (data && typeof data.active_sessions === "number") {
        setActiveChatsCount(data.active_sessions);
      }
    } catch (error) {
      console.error("Error fetching active chats count:", error);
    }
  }, []);

  useEffect(() => {
    if (user && (user.role === 'admin' || user.role === 'super_admin')) {
      fetchAiAgents();
    }
  }, [fetchAiAgents, user]);

  // Handle Agent Validation Separately
  useEffect(() => {
    if (aiAgents.length > 0) {
      // Robust multi-role agent restriction:
      // 1. Admins see all agents.
      // 2. Regular client users are strictly locked to their database-assigned agent_id.
      let assignedIds = [];
      if (user) {
        if (user.role === 'super_admin') {
          assignedIds = aiAgents.map(p => String(p.id));
        } else if (user.role === 'admin') {
          const userAssigned = user.assigned_ai_agents || [];
          assignedIds = userAssigned.map(id => String(id));
        } else if (user.role === 'user') {
          assignedIds = user.agent_id ? [String(user.agent_id)] : (aiAgents.length > 0 ? [String(aiAgents[0].id)] : []);
        } else {
          const userAssigned = user.assigned_ai_agents;
          assignedIds = (userAssigned && userAssigned.length > 0)
            ? userAssigned.map(id => String(id))
            : (user.agent_id ? [String(user.agent_id)] : [String(aiAgents[0].id)]);
        }
      } else {
        assignedIds = aiAgents.map(p => String(p.id));
      }

      const activeStr = activeAiAgentId ? String(activeAiAgentId) : "";
      const isCurrentValid = activeStr && assignedIds.some(id => String(id) === activeStr);

      if (!isCurrentValid && assignedIds.length > 0) {
        const defaultId = String(assignedIds[0]);
        console.log(`RBAC: Resetting active agent to authorized default: ${defaultId}`);
        setActiveAiAgentId(defaultId);
        localStorage.setItem("activeAiAgentId", defaultId);
        api.setAiAgentId(defaultId);
      }
    }
  }, [aiAgents, activeAiAgentId, user]);

  useEffect(() => {
    if (activeAiAgentId) {
      api.setAiAgentId(activeAiAgentId);
    }
  }, [activeAiAgentId]);

  useEffect(() => {
    if (user && activeAiAgentId) {
      // Initial fetch
      fetchActiveChatsCount();

      // Poll every 30 seconds for live updates
      const interval = setInterval(() => {
        fetchActiveChatsCount();
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [fetchActiveChatsCount, user, activeAiAgentId]);

  useEffect(() => {
    localStorage.setItem("activeAiAgentId", activeAiAgentId);
    api.setAiAgentId(activeAiAgentId);
  }, [activeAiAgentId]);

  return (
    <Routes key={activeAiAgentId}>
      {/* Public routes */}
      <Route path="/docs" element={<Documentation />} />
      <Route path="/data-deletion" element={<Documentation />} />
      <Route path="/data-deletion-instructions" element={<Documentation />} />

      <Route path="/login" element={
        !user ? (
          <Login onLogin={handleLogin} />
        ) : (
          <Navigate to={user.role === 'human_agent' ? '/human-agent-chat' : '/chat'} replace />
        )
      } />

      {!user ? (
        <Route path="/*" element={<Navigate to="/login" replace />} />
      ) : user.role === 'human_agent' ? (
        <>
          <Route
            path="/human-agent-chat"
            element={
              <HumanAgentChatInterface onLogout={handleLogout} user={user} hideHeader={false} />
            }
          />
          <Route path="/*" element={<Navigate to="/human-agent-chat" replace />} />
        </>
      ) : (
        <>
          <Route path="/" element={<Navigate to="/chat" replace />} />

          <Route
            path="/chat"
            element={
              <AdminPanelWrapper
                onLogout={handleLogout}
                user={user}
                activeAiAgentId={activeAiAgentId}
                aiAgents={aiAgents}
              />
            }
          />

          {/* Admin Dashboard */}
          <Route
            path="/admin"
            element={
              <div className="flex h-screen bg-gray-50">
                {!isHumanAgent && (
                  <Sidebar
                    user={user}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    isCollapsed={sidebarCollapsed}
                    setIsCollapsed={setSidebarCollapsed}
                    activeChatsCount={activeChatsCount}
                    activeProduct={activeProduct}
                    setActiveProduct={setActiveProduct}
                  />
                )}
                <div className="flex-1 flex flex-col min-w-0">
                  <main className="flex-1 overflow-y-auto custom-scrollbar bg-gray-50 relative">
                    <Navbar
                      onToggleSidebar={() =>
                        setSidebarCollapsed(!sidebarCollapsed)
                      }
                      sidebarCollapsed={sidebarCollapsed}
                      activeAiAgentId={activeAiAgentId}
                      setActiveAiAgentId={setActiveAiAgentId}
                      aiAgents={aiAgents}
                      activeChatsCount={activeChatsCount}
                    />
                    <AdminDashboard
                      activeAiAgentId={activeAiAgentId}
                      aiAgents={aiAgents}
                      setActiveTab={setActiveTab}
                      fetchAiAgents={fetchAiAgents}
                      setActiveAiAgentId={setActiveAiAgentId}
                      activeChatsCount={activeChatsCount}
                    />
                  </main>
                </div>
              </div>
            }
          />

          {/* Skills */}
          <Route
            path="/skills"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                content={<Skills activeAiAgentId={activeAiAgentId} />}
                wide={true}
              />
            }
          />

          {/* Logo */}
          <Route
            path="/logo"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                content={<LogoManagement />}
                wide={true}
              />
            }
          />

          {/* Smart FAQ */}
          <Route
            path="/smart-faq"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                content={<KnowledgeBase />}
                wide={true}
              />
            }
          />

          {/* AI Evaluations */}
          <Route
            path="/evaluations"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                content={<Evaluations activeAiAgentId={activeAiAgentId} />}
                wide={true}
              />
            }
          />

          {/* Documents */}
          <Route
            path="/documents"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                content={<DocumentUpload />}
                wide={true}
              />
            }
          />

          {/* Knowledge Graph */}
          <Route
            path="/knowledge-graph"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                content={<KnowledgeGraph activeAiAgentId={activeAiAgentId} />}
                wide={true}
              />
            }
          />

          {/* Widget */}
          <Route
            path="/widget"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                wide={true}
                content={<WidgetIntegration />}
              />
            }
          />

          {/* Widget Preview */}
          <Route
            path="/widget-preview"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                content={<WidgetPreview />}
                wide={true}
              />
            }
          />

          {/* Widget Preview Display - Fullscreen without sidebar */}
          <Route
            path="/widget-preview/display"
            element={<WidgetPreviewDisplay />}
          />

          {/* Instructions */}
          <Route
            path="/instructions"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                content={<PromptVersioning activeAiAgentId={activeAiAgentId} />}
                wide={true}
              />
            }
          />

          {/* Models: hidden (Kambaa chooses the AI model; the backend refuses changes unless MODEL_SELECTION_ENABLED) */}
          <Route path="/models" element={<Navigate to="/" replace />} />

          {/* Agents */}
          <Route
            path="/ai-agents"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                content={<AiAgents setActiveAiAgentId={setActiveAiAgentId} />}
                wide={true}
              />
            }
          />

          {/* Human Agents */}
          <Route
            path="/human-agents"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                content={<HumanAgents aiAgents={aiAgents} />}
                wide={true}
              />
            }
          />

          {/* Human Agent Groups */}
          <Route
            path="/human-agent-groups"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                content={<HumanAgentGroups />}
                wide={true}
              />
            }
          />



          {/* API Tools */}
          <Route
            path="/api-tools"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                content={<ApiToolsManagement />}
                wide={true}
              />
            }
          />


          {/* Campaigns */}
          <Route
            path="/campaigns"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                content={<CampaignManagement />}
                wide={true}
              />
            }
          />

          {/* Business Flows */}
          <Route
            path="/business-flows"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                activeProduct={activeProduct}
                setActiveProduct={setActiveProduct}
                content={<BusinessFlows />}
                wide={true}
              />
            }
          />

          {/* Visual Flow Builder */}
          <Route
            path="/flow-builder"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                activeProduct={activeProduct}
                setActiveProduct={setActiveProduct}
                content={<FlowBuilder />}
                wide={true}
              />
            }
          />
          <Route
            path="/flow-builder/new"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                activeProduct={activeProduct}
                setActiveProduct={setActiveProduct}
                content={<FlowBuilder />}
                wide={true}
              />
            }
          />

          {/* Webhooks */}
          <Route
            path="/webhooks"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                content={<WebhookManagement />}
                wide={true}
              />
            }
          />

          {/* Domains */}
          <Route
            path="/domains"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                content={<DomainManagement />}
                wide={true}
              />
            }
          />

          {/* Integrations */}
          <Route
            path="/integrations"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                content={<Integrations />}
                wide={true}
              />
            }
          />

          {/* Freshchat Config */}
          <Route
            path="/freshchat-config"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                wide={true}
                content={<FreshchatConfig />}
              />
            }
          />

          {/* Telegram Config */}
          <Route
            path="/telegram-config"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                wide={true}
                content={<TelegramConfig />}
              />
            }
          />

          {/* Instagram Config */}
          <Route
            path="/instagram-config"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                wide={true}
                content={<InstagramConfig />}
              />
            }
          />

          {/* Instagram with the client's own Meta app */}
          <Route
            path="/instagram-own-config"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                wide={true}
                content={<InstagramOwnAppConfig />}
              />
            }
          />

          {/* WhatsApp Config */}
          <Route
            path="/whatsapp-config"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                wide={true}
                content={<WhatsAppConfig />}
              />
            }
          />

          {/* WhatsApp Templates */}
          <Route
            path="/whatsapp-templates"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                wide={true}
                content={<WhatsAppTemplates activeAiAgentId={activeAiAgentId} />}
              />
            }
          />

          {/* Slack Config */}
          <Route
            path="/slack-config"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                wide={true}
                content={<SlackConfig />}
              />
            }
          />

          {/* Email Config */}
          <Route
            path="/email-config"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                wide={true}
                content={<EmailConfig />}
              />
            }
          />

          {/* Zendesk Config */}
          <Route
            path="/zendesk-config"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                wide={true}
                content={<ZendeskConfig activeAiAgentId={activeAiAgentId} />}
              />
            }
          />

          {/* Freshdesk Config */}
          <Route
            path="/freshdesk-config"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                wide={true}
                content={<FreshdeskConfig activeAiAgentId={activeAiAgentId} />}
              />
            }
          />

          {/* Shopify Config */}
          <Route
            path="/shopify-config"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                wide={true}
                content={<ShopifyConfig activeAiAgentId={activeAiAgentId} />}
              />
            }
          />


          {/* Tenant Agents */}
          <Route
            path="/ai-agents"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                content={<AiAgents />}
              />
            }
          />

          <Route
            path="/analytics"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                content={<Analytics />}
                wide={true}
              />
            }
          />

          <Route
            path="/user-management"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                content={<UserManagement agents={aiAgents} aiAgents={aiAgents} />}
                wide={true}
              />
            }
          />

          <Route
            path="/billing"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                content={<PaymentPage userName={user?.username || "admin"} tenantId={user?.tenant_id} />}
                wide={true}
              />
            }
          />

          <Route
            path="/contacts"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                content={<Contacts />}
                wide={true}
              />
            }
          />

          <Route
            path="/segments"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                content={<Segments />}
                wide={true}
              />
            }
          />

          <Route
            path="/reports"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                content={<Reports />}
                wide={true}
              />
            }
          />

          <Route
            path="/contacts/fields"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                content={<ManageFields />}
                wide={true}
              />
            }
          />

          <Route
            path="/contacts/:id"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                content={<ContactDetail />}
                wide={true}
              />
            }
          />

          <Route
            path="/human-agent-chat"
            element={
              <PageWrapper
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                sidebarCollapsed={sidebarCollapsed}
                setSidebarCollapsed={setSidebarCollapsed}
                activeAiAgentId={activeAiAgentId}
                setActiveAiAgentId={setActiveAiAgentId}
                aiAgents={aiAgents}
                fetchAiAgents={fetchAiAgents}
                activeChatsCount={activeChatsCount}
                content={<HumanAgentChatInterface onLogout={handleLogout} user={user} hideHeader={false} />}
                wide={true}
                disableScroll={true}
              />
            }
          />
        </>
      )}
    </Routes>
  );
}

/* ---------------------------------------------------------
   REUSABLE PAGE WRAPPER
--------------------------------------------------------- */
function PageWrapper({
  activeTab,
  setActiveTab,
  sidebarCollapsed,
  setSidebarCollapsed,
  activeAiAgentId,
  setActiveAiAgentId,
  aiAgents,
  fetchAiAgents,
  content,
  activeChatsCount,
  wide = false,
  activeProduct = "agents",
  setActiveProduct,
  disableScroll = false,
}) {


  const userStr = sessionStorage.getItem("user") || localStorage.getItem("user");
  const user = userStr ? JSON.parse(userStr) : null;
  const userObj = user;
  const isHumanAgent = user && user.role === 'human_agent';
  const location = useLocation();
  const mainRef = React.useRef(null);

  React.useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTop = 0;
    }
  }, [location.pathname]);

  return (
    <div className="flex h-screen bg-gray-50">
      {!isHumanAgent && (
        <Sidebar
          user={userObj}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          isCollapsed={sidebarCollapsed}
          setIsCollapsed={setSidebarCollapsed}
          activeChatsCount={activeChatsCount}
          activeProduct={activeProduct}
          setActiveProduct={setActiveProduct}
        />
      )}
      <div className="flex-1 flex flex-col min-w-0">
        <main ref={mainRef} className={`flex-1 ${disableScroll ? "overflow-hidden flex flex-col" : "overflow-y-auto overflow-x-hidden"} custom-scrollbar bg-white min-w-0 relative`}>
          <Navbar
            onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
            sidebarCollapsed={sidebarCollapsed}
            activeAiAgentId={activeAiAgentId}
            setActiveAiAgentId={setActiveAiAgentId}
            aiAgents={aiAgents}
            activeChatsCount={activeChatsCount}
          />
          <div className={`${wide ? "p-0 max-w-full" : "p-8 max-w-5xl mx-auto"} ${disableScroll ? "flex-1 min-h-0" : ""}`}>
            {React.cloneElement(content, { activeAiAgentId, fetchAiAgents, aiAgents })}
          </div>
        </main>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------
   APP ROOT
--------------------------------------------------------- */
function App() {
  return (
    <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
      <AppContent />
    </Router>
  );
}

export default App;
